<template>
  <div class="page wochen-page">

    <div class="wochen-kopf">
      <button type="button" class="nav-arrow" @click="VorherigeWoche" aria-label="Vorherige Woche">
        <font-awesome-icon icon="fa-solid fa-chevron-left" />
      </button>
      <div class="wochen-kopf-mitte">
        <div class="wochen-titel">Wochenplanung</div>
        <div class="wochen-label">KW {{ Kalenderwoche }} · {{ Wochenbereich }}</div>
        <div class="kopf-chips">
          <button type="button" class="heute-chip" @click="ZuHeuteSpringen">
            <font-awesome-icon icon="fa-solid fa-calendar-day" />
            Heute
          </button>
          <button type="button" class="menue-chip" @click="MenueOffen = true">
            <font-awesome-icon icon="fa-solid fa-utensils" />
            Menü
          </button>
        </div>
      </div>
      <button type="button" class="nav-arrow" @click="NaechsteWoche" aria-label="Nächste Woche">
        <font-awesome-icon icon="fa-solid fa-chevron-right" />
      </button>
    </div>

    <div class="tage-grid">
      <button
        v-for="(Tag, Index) in Tage"
        :key="Index"
        type="button"
        class="tag-zelle"
        :class="{ aktiv: AusgewaehlterTag === Index, 'hat-plan': TagHatPlan(Index), heute: IstHeute(Index) }"
        @click="TagAuswaehlen(Index)"
      >
        <span class="tag-kuerzel">{{ Tag.KurzName }}</span>
        <span class="tag-tag">{{ Tag.Datum.getDate() }}</span>
        <span v-if="TagHatPlan(Index)" class="tag-dot" aria-hidden="true"></span>
      </button>
    </div>

    <div v-if="AusgewaehlterTag !== null" class="tag-details">
      <div class="tag-details-kopf">
        <span class="tag-details-datum">{{ TagHeaderText }}</span>
        <div class="personen-stepper" title="Wie viele Personen essen an diesem Tag mit?">
          <button
            type="button"
            class="personen-btn"
            :disabled="TagPersonen <= 1"
            aria-label="Weniger Personen"
            @click="TagPersonenAendern(-1)"
          >
            <font-awesome-icon icon="fa-solid fa-minus" />
          </button>
          <span class="personen-wert">
            <font-awesome-icon icon="fa-solid fa-users" />
            {{ TagPersonen }}
          </span>
          <button
            type="button"
            class="personen-btn"
            aria-label="Mehr Personen"
            @click="TagPersonenAendern(1)"
          >
            <font-awesome-icon icon="fa-solid fa-plus" />
          </button>
        </div>
        <span v-if="GeplanteAnzahl > 0" class="tag-geplant-badge">{{ GeplanteAnzahl }} geplant</span>
      </div>

      <p v-if="Fehler" class="error-message">{{ Fehler }}</p>

      <div class="gaenge-liste">
        <div v-for="Gruppe in MahlzeitenGruppen" :key="Gruppe.Titel" class="mahlzeit-gruppe">
          <div class="mahlzeit-gruppe-header">
            <span class="gruppe-titel">{{ Gruppe.Titel }}</span>
            <span v-if="GruppeGeplanteAnzahl(Gruppe) > 0" class="gruppe-badge">
              {{ GruppeGeplanteAnzahl(Gruppe) }} geplant
            </span>
          </div>

          <div v-for="Gang in Gruppe.Slots" :key="Gang" class="gang-zeile">
            <img
              v-if="GangBildUrl(Gang)"
              :src="GangBildUrl(Gang)"
              class="gang-icon gang-icon--bild"
              alt=""
              @error="$event.target.style.display = 'none'"
            />
            <div v-else class="gang-icon" :style="{ background: GangKonfig[Gang].BgFarbe }">
              {{ GangKonfig[Gang].Emoji }}
            </div>
            <div class="gang-inhalt">
              <span class="gang-label">{{ Gruppe.Slots.length > 1 ? Gang : 'Rezept' }}</span>
              <button
                v-if="PlanFuerTag(AusgewaehlterTag, Gang)"
                class="gang-rezept-btn"
                @click="RezeptAuswaehlen(Gang)"
              >
                {{ PlanFuerTag(AusgewaehlterTag, Gang).RezeptName }}
              </button>
              <span v-else class="gang-platzhalter">Rezept hinzufügen ...</span>
            </div>
            <div class="gang-aktionen">
              <template v-if="PlanFuerTag(AusgewaehlterTag, Gang)">
                <button class="gang-aendern-btn" @click="RezeptAuswaehlen(Gang)">ändern</button>
                <button
                  class="gang-loeschen-btn"
                  @click="PlanLoeschen(Gang)"
                  :aria-label="Gang + ' entfernen'"
                >
                  <font-awesome-icon icon="fa-solid fa-xmark" />
                </button>
              </template>
              <button v-else class="gang-plus-btn" @click="RezeptAuswaehlen(Gang)">
                <font-awesome-icon icon="fa-solid fa-plus" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Menü der Woche (Speisekarte als durchblätterbares Buch, ein Tag pro Seite) -->
    <Teleport to="body">
      <div v-if="MenueOffen" class="overlay menue-overlay" @click.self="MenueOffen = false">
        <div class="speisekarte speisekarte--buch" role="dialog" aria-modal="true" aria-label="Menü der Woche">
          <div class="karte-ornament">✦</div>
          <h2 class="karte-titel">Menü der Woche</h2>
          <p class="karte-untertitel">
            Kalenderwoche {{ Kalenderwoche }}
            <span class="karte-seitenzahl">· Seite {{ BuchSeite + 1 }}/{{ BuchSeiten.length }}</span>
          </p>
          <div class="karte-trenner">
            <span class="karte-trenner-linie"></span>
            <span class="karte-trenner-raute">◆</span>
            <span class="karte-trenner-linie"></span>
          </div>

          <div class="buch-viewport">
            <button
              v-if="BuchSeite > 0"
              type="button"
              class="buch-pfeil buch-pfeil--links"
              @click="SeiteZurueck"
              aria-label="Vorherige Seite"
            >
              <font-awesome-icon icon="fa-solid fa-chevron-left" />
            </button>
            <button
              v-if="BuchSeite < BuchSeiten.length - 1"
              type="button"
              class="buch-pfeil buch-pfeil--rechts"
              @click="SeiteVor"
              aria-label="Nächste Seite"
            >
              <font-awesome-icon icon="fa-solid fa-chevron-right" />
            </button>

            <transition :name="BuchRichtung === 'zurueck' ? 'buch-seite-zurueck' : 'buch-seite-vor'" mode="out-in">
              <div :key="BuchSeite" class="buch-seite">
                <template v-if="AktuelleSeite.Typ === 'Epilog'">
                  <div class="buch-epilog">
                    <span class="buch-epilog-ornament">✦</span>
                    <p class="buch-epilog-text">Das war's für diese Woche.</p>
                    <button type="button" class="buch-epilog-link" @click="NaechstesBuchOeffnen">
                      Weiter zu Kalenderwoche {{ NaechsteKalenderwoche }}
                      <font-awesome-icon icon="fa-solid fa-arrow-right" />
                    </button>
                  </div>
                </template>
                <template v-else-if="AktuelleSeite.Typ === 'Leer'">
                  <p class="karte-leer">Noch nichts geplant</p>
                </template>
                <template v-else>
                  <p class="buch-tag-titel">{{ AktuelleSeite.TagName }}</p>
                  <div class="karte-gaenge-liste">
                    <button
                      v-for="Gang in AktuelleSeite.Gaenge"
                      :key="Gang.Kategorie"
                      type="button"
                      class="karte-gang-buch"
                      @click="ZumTagSpringen(AktuelleSeite.TagIndex)"
                    >
                      <img
                        v-if="Gang.BildUrl"
                        :src="Gang.BildUrl"
                        class="karte-gang-bild"
                        alt=""
                        loading="lazy"
                        @error="$event.target.style.display = 'none'"
                      />
                      <span v-else class="karte-gang-bild karte-gang-bild--platzhalter">
                        <font-awesome-icon icon="fa-solid fa-utensils" />
                      </span>
                      <span class="karte-gang-info">
                        <span class="karte-gericht-kategorie">{{ Gang.Kategorie }}</span>
                        <span class="karte-gericht">{{ Gang.RezeptName }}</span>
                      </span>
                    </button>
                  </div>
                </template>
              </div>
            </transition>
          </div>

          <div class="karte-ornament karte-ornament--unten">✦</div>
          <button type="button" class="karte-schliessen" @click="MenueOffen = false">Schließen</button>
        </div>
      </div>
    </Teleport>

    <!-- Rezept-Picker -->
    <Teleport to="body">
      <div v-if="RezeptPickerOffen" class="overlay" @click.self="RezeptPickerSchliessen">
        <div class="bottom-sheet" role="dialog" aria-modal="true" :aria-label="'Rezept für ' + AktuellerGang + ' auswählen'">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">{{ AktuellerGang }}: Rezept auswählen</h3>
          <input
            v-model="RezeptSuche"
            type="text"
            placeholder="Rezept suchen…"
            class="liste-suche-feld"
            autocomplete="off"
          />
          <div class="picker-liste">
            <div v-if="RezepteDesGangs.length === 0" class="picker-kein-rezept">
              <p class="hint-message">Noch keine {{ AktuellerGang }}-Rezepte vorhanden.</p>
              <router-link
                :to="{ path: '/rezepte/neu', query: { kategorie: AktuellerGang } }"
                class="btn btn-secondary neu-rezept-link"
                @click="RezeptPickerSchliessen"
              >
                <font-awesome-icon icon="fa-solid fa-plus" />
                {{ AktuellerGang }}-Rezept anlegen
              </router-link>
            </div>
            <template v-else>
              <p v-if="GefilterteRezepteZuKochen.length === 0 && GefilterteRezepteEingefroren.length === 0" class="hint-message">
                Kein passendes Rezept gefunden.
              </p>
              <template v-if="GefilterteRezepteEingefroren.length > 0">
                <p class="picker-gruppen-titel">🧊 Eingefroren</p>
                <button
                  v-for="Rezept in GefilterteRezepteEingefroren"
                  :key="Rezept.Id"
                  class="picker-eintrag picker-eintrag--eingefroren"
                  @click="RezeptWaehlen(Rezept)"
                >
                  <img
                    v-if="Rezept.HatBild"
                    :src="RezeptBildUrl(Rezept)"
                    class="mini-bild"
                    alt=""
                    loading="lazy"
                    @error="$event.target.style.display = 'none'"
                  />
                  <span class="picker-name">{{ Rezept.Name }}</span>
                  <span class="picker-kategorie">{{ Rezept.Kategorien.join(', ') }}</span>
                </button>
              </template>
              <template v-if="GefilterteRezepteZuKochen.length > 0">
                <p class="picker-gruppen-titel">Zu kochen</p>
                <button
                  v-for="Rezept in GefilterteRezepteZuKochen"
                  :key="Rezept.Id"
                  class="picker-eintrag"
                  @click="RezeptWaehlen(Rezept)"
                >
                  <img
                    v-if="Rezept.HatBild"
                    :src="RezeptBildUrl(Rezept)"
                    class="mini-bild"
                    alt=""
                    loading="lazy"
                    @error="$event.target.style.display = 'none'"
                  />
                  <span class="picker-name">{{ Rezept.Name }}</span>
                  <span class="picker-kategorie">{{ Rezept.Kategorien.join(', ') }}</span>
                </button>
              </template>
            </template>
          </div>
          <button type="button" class="btn btn-secondary" @click="RezeptPickerSchliessen">Abbrechen</button>
        </div>
      </div>
    </Teleport>

    <!-- Zutaten-Picker -->
    <Teleport to="body">
      <div v-if="ZutatenPickerOffen" class="overlay" @click.self="ZutatenPickerSchliessen">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Zutaten zur Einkaufsliste hinzufügen">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">Zutaten zur Einkaufsliste?</h3>
          <p class="hint-message sheet-rezept-name">
            {{ AktuellesRezept && AktuellesRezept.Name }}
            · für {{ TagPersonen }} {{ TagPersonen === 1 ? 'Person' : 'Personen' }}
            <template v-if="AktuellesRezept && AktuellesRezept.Personen && AktuellesRezept.Personen !== TagPersonen">
              (Rezept: {{ AktuellesRezept.Personen }})
            </template>
          </p>

          <template v-if="AktuellesRezept && AktuellesRezept.Zutaten && AktuellesRezept.Zutaten.length">
            <div class="zutaten-check-liste">
              <label
                v-for="Z in AktuellesRezept.Zutaten"
                :key="Z.ZutatId"
                class="zutat-check-zeile"
                :class="{ 'zutat-check-zeile--eingefroren': ZutatEingefroren(Z.ZutatId) }"
              >
                <input type="checkbox" :value="Z.ZutatId" v-model="GewaehlteZutatenIds" />
                <img
                  v-if="ZutatBildUrlWennVorhanden(Z.ZutatId)"
                  :src="ZutatBildUrlWennVorhanden(Z.ZutatId)"
                  class="mini-bild mini-bild--klein"
                  alt=""
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
                <span class="zutat-check-name">
                  <template v-if="ZutatEingefroren(Z.ZutatId)">🧊 </template>{{ ZutatNameFinden(Z.ZutatId, Z.Name) }}
                </span>
                <span class="zutat-check-menge">{{ ZutatMengeText(Z) }}</span>
              </label>
            </div>

            <div v-if="Einkaufslisten.length > 1" class="ziel-liste-zeile">
              <label class="ziel-liste-label" for="ziel-liste">Auf welche Einkaufsliste?</label>
              <select id="ziel-liste" v-model.number="ZielListeId" class="ziel-liste-select">
                <option v-for="Liste in Einkaufslisten" :key="Liste.Id" :value="Liste.Id">{{ Liste.Name }}</option>
              </select>
            </div>

            <p v-if="ZutatenFehler" class="error-message">{{ ZutatenFehler }}</p>
            <div class="button-row">
              <button
                type="button"
                class="btn btn-primary"
                @click="ZutatenHinzufuegen"
                :disabled="GewaehlteZutatenIds.length === 0 || ZutatenLaedt"
              >
                <span class="btn-inhalt">
                  <font-awesome-icon icon="fa-solid fa-cart-plus" />
                  Hinzufügen ({{ GewaehlteZutatenIds.length }})
                </span>
              </button>
              <button type="button" class="btn btn-secondary" @click="ZutatenPickerSchliessen">Überspringen</button>
            </div>
          </template>
          <template v-else>
            <p class="hint-message">Dieses Rezept hat keine Zutaten hinterlegt.</p>
            <button type="button" class="btn btn-secondary" @click="ZutatenPickerSchliessen">Schließen</button>
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  WochenplanungLaden,
  Wochenplanpositionen,
  WochenplanpositionSpeichern,
  WochenplanpositionLoeschen
} from '../composables/useWochenplanung'
import { RezepteLaden, Rezepte } from '../composables/useRezepte'
import {
  ZutatenLaden,
  ZutatFinden,
  ZutatZurEinkaufslisteHinzufuegen,
  Einkaufslisten,
  EinkaufslistenLaden,
  AktiveEinkaufsliste
} from '../composables/useZutaten'
import { PackungsBedarf, MengeFormatieren } from '../composables/useSkalierung'
import { BildUrl } from '../composables/useBilder'

const MahlzeitenGruppen = [
  { Titel: 'Frühstück',   Slots: ['Frühstück'] },
  { Titel: 'Mittagessen', Slots: ['Vorspeise', 'Hauptgang', 'Nachspeise'] },
  { Titel: 'Abendessen',  Slots: ['Abendessen'] }
]

const GangKonfig = {
  'Frühstück':  { Emoji: '☀️', BgFarbe: '#FEF3C7' },
  'Vorspeise':  { Emoji: '🥗', BgFarbe: '#D1FAE5' },
  'Hauptgang':  { Emoji: '🍽️', BgFarbe: '#DBEAFE' },
  'Nachspeise': { Emoji: '🍰', BgFarbe: '#FCE7F3' },
  'Abendessen': { Emoji: '🌙', BgFarbe: '#EDE9FE' },
}

const Wochentage = [
  { Name: 'Montag',     KurzName: 'Mo' },
  { Name: 'Dienstag',   KurzName: 'Di' },
  { Name: 'Mittwoch',   KurzName: 'Mi' },
  { Name: 'Donnerstag', KurzName: 'Do' },
  { Name: 'Freitag',    KurzName: 'Fr' },
  { Name: 'Samstag',    KurzName: 'Sa' },
  { Name: 'Sonntag',    KurzName: 'So' }
]

const Monate = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember']

function MontagDerWoche(Datum) {
  const D = new Date(Datum)
  const TagIndex = D.getDay() || 7
  D.setDate(D.getDate() - TagIndex + 1)
  D.setHours(0, 0, 0, 0)
  return D
}

function IsoKalenderwoche(Datum) {
  const D = new Date(Date.UTC(Datum.getFullYear(), Datum.getMonth(), Datum.getDate()))
  const TagIndex = D.getUTCDay() || 7
  D.setUTCDate(D.getUTCDate() + 4 - TagIndex)
  const Jahresstart = new Date(Date.UTC(D.getUTCFullYear(), 0, 1))
  return Math.ceil(((D - Jahresstart) / 86400000 + 1) / 7)
}

export default {
  name: 'WochenplanungView',
  data() {
    return {
      WochenStart: MontagDerWoche(new Date()),
      AusgewaehlterTag: null,
      MenueOffen: false,
      BuchSeite: 0,
      BuchRichtung: 'vor',
      MahlzeitenGruppen,
      GangKonfig,
      Wochenplanpositionen,
      Rezepte,
      Einkaufslisten,
      TagPersonen: 4,
      ZielListeId: null,
      RezeptPickerOffen: false,
      ZutatenPickerOffen: false,
      AktuellerGang: null,
      RezeptSuche: '',
      AktuellesRezept: null,
      GewaehlteZutatenIds: [],
      ZutatenFehler: '',
      ZutatenLaedt: false,
      Fehler: ''
    }
  },
  async mounted() {
    await Promise.all([WochenplanungLaden(), RezepteLaden(), ZutatenLaden(), EinkaufslistenLaden()])
  },
  watch: {
    AusgewaehlterTag(Neu) {
      if (Neu === null) return
      // Personenzahl des Tages aus bereits geplanten Gängen übernehmen, sonst letzte Eingabe behalten
      const Datum = this.DatumZuString(this.Tage[Neu].Datum)
      const Position = this.Wochenplanpositionen.find(p => p.Datum === Datum)
      if (Position && Position.Personen) {
        this.TagPersonen = Position.Personen
      }
    },
    MenueOffen(Offen) {
      if (Offen) {
        this.BuchSeite = 0
        this.BuchRichtung = 'vor'
      }
    }
  },
  computed: {
    Tage() {
      return Wochentage.map(({ Name, KurzName }, Index) => {
        const Datum = new Date(this.WochenStart)
        Datum.setDate(Datum.getDate() + Index)
        return { Name, KurzName, Datum }
      })
    },
    Kalenderwoche() {
      return IsoKalenderwoche(this.WochenStart)
    },
    Wochenbereich() {
      const Ende = new Date(this.WochenStart)
      Ende.setDate(Ende.getDate() + 6)
      return `${this.FormatKurz(this.WochenStart)} – ${this.FormatMitJahr(Ende)}`
    },
    TagHeaderText() {
      if (this.AusgewaehlterTag === null) return ''
      const Tag = this.Tage[this.AusgewaehlterTag]
      const D = Tag.Datum
      return `${Tag.KurzName}, ${D.getDate()}. ${Monate[D.getMonth()]}`
    },
    GeplanteAnzahl() {
      if (this.AusgewaehlterTag === null) return 0
      const Datum = this.DatumZuString(this.Tage[this.AusgewaehlterTag].Datum)
      return this.Wochenplanpositionen.filter(p => p.Datum === Datum).length
    },
    WochenSpeisekarte() {
      // Alle Gaenge (nicht nur Hauptgang) pro Tag, in fester Reihenfolge; Tage ohne jede Planung entfallen.
      return this.Tage
        .map((Tag, Index) => {
          const Gaenge = Object.keys(GangKonfig)
            .map(Gang => {
              const Plan = this.PlanFuerTag(Index, Gang)
              if (!Plan) return null
              const Rezept = this.Rezepte.find(r => r.Id === Plan.RezeptId)
              return {
                Kategorie: Gang,
                RezeptName: Plan.RezeptName,
                BildUrl: Rezept && Rezept.HatBild ? this.RezeptBildUrl(Rezept) : null
              }
            })
            .filter(Boolean)
          return Gaenge.length > 0 ? { TagIndex: Index, TagName: Tag.Name, Gaenge } : null
        })
        .filter(Boolean)
    },
    // Buch-Seiten fuer die durchblaetterbare Speisekarte: eine Seite pro Tag mit Planung
    // (mind. eine "leer"-Seite, falls die Woche komplett unbeplant ist), plus eine Epilog-Seite
    // am Ende, die auf die naechste Kalenderwoche verweist - das Blaettern bleibt so auf die
    // aktuell angezeigte KW beschraenkt, ein Wochenwechsel passiert nur ueber diesen bewussten Klick.
    BuchSeiten() {
      const Tage = this.WochenSpeisekarte
      const Seiten = Tage.length > 0
        ? Tage.map(Tag => ({ ...Tag, Typ: 'Tag' }))
        : [{ Typ: 'Leer' }]
      Seiten.push({ Typ: 'Epilog' })
      return Seiten
    },
    AktuelleSeite() {
      const Index = Math.min(this.BuchSeite, this.BuchSeiten.length - 1)
      return this.BuchSeiten[Index]
    },
    NaechsteKalenderwoche() {
      const Neu = new Date(this.WochenStart)
      Neu.setDate(Neu.getDate() + 7)
      return IsoKalenderwoche(Neu)
    },
    RezepteDesGangs() {
      return this.Rezepte.filter(r => (r.Kategorien || []).includes(this.AktuellerGang))
    },
    GefilterteRezepte() {
      if (!this.RezeptSuche.trim()) return this.RezepteDesGangs
      const S = this.RezeptSuche.toLowerCase()
      return this.RezepteDesGangs.filter(r => r.Name.toLowerCase().includes(S))
    },
    GefilterteRezepteZuKochen() {
      return this.GefilterteRezepte.filter(r => !r.Eingefroren)
    },
    GefilterteRezepteEingefroren() {
      return this.GefilterteRezepte.filter(r => r.Eingefroren)
    },
    PersonenFaktor() {
      const RezeptPersonen = this.AktuellesRezept?.Personen || 4
      return this.TagPersonen / Math.max(1, RezeptPersonen)
    }
  },
  methods: {
    VorherigeWoche() {
      const Neu = new Date(this.WochenStart)
      Neu.setDate(Neu.getDate() - 7)
      this.WochenStart = Neu
      this.AusgewaehlterTag = null
    },
    NaechsteWoche() {
      const Neu = new Date(this.WochenStart)
      Neu.setDate(Neu.getDate() + 7)
      this.WochenStart = Neu
      this.AusgewaehlterTag = null
    },
    TagAuswaehlen(Index) {
      this.AusgewaehlterTag = this.AusgewaehlterTag === Index ? null : Index
      this.Fehler = ''
    },
    IstHeute(Index) {
      return this.DatumZuString(this.Tage[Index].Datum) === this.DatumZuString(new Date())
    },
    ZuHeuteSpringen() {
      const Heute = new Date()
      this.WochenStart = MontagDerWoche(Heute)
      this.AusgewaehlterTag = (Heute.getDay() || 7) - 1
      this.Fehler = ''
    },
    ZumTagSpringen(Index) {
      this.MenueOffen = false
      this.AusgewaehlterTag = Index
      this.Fehler = ''
      this.$nextTick(() => {
        const Details = this.$el.querySelector('.tag-details')
        if (Details) Details.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    SeiteVor() {
      if (this.BuchSeite < this.BuchSeiten.length - 1) {
        this.BuchRichtung = 'vor'
        this.BuchSeite++
      }
    },
    SeiteZurueck() {
      if (this.BuchSeite > 0) {
        this.BuchRichtung = 'zurueck'
        this.BuchSeite--
      }
    },
    NaechstesBuchOeffnen() {
      const Neu = new Date(this.WochenStart)
      Neu.setDate(Neu.getDate() + 7)
      this.WochenStart = Neu
      this.BuchRichtung = 'vor'
      this.BuchSeite = 0
    },
    FormatKurz(Datum) {
      return `${String(Datum.getDate()).padStart(2, '0')}.${String(Datum.getMonth() + 1).padStart(2, '0')}.`
    },
    FormatMitJahr(Datum) {
      return `${this.FormatKurz(Datum)}${String(Datum.getFullYear()).slice(-2)}`
    },
    DatumZuString(Datum) {
      const Jahr  = Datum.getFullYear()
      const Monat = String(Datum.getMonth() + 1).padStart(2, '0')
      const Tag   = String(Datum.getDate()).padStart(2, '0')
      return `${Jahr}-${Monat}-${Tag}`
    },
    PlanFuerTag(TagIndex, Gang) {
      const Datum = this.DatumZuString(this.Tage[TagIndex].Datum)
      return this.Wochenplanpositionen.find(p => p.Datum === Datum && p.Kategorie === Gang) || null
    },
    TagHatPlan(TagIndex) {
      const Datum = this.DatumZuString(this.Tage[TagIndex].Datum)
      return this.Wochenplanpositionen.some(p => p.Datum === Datum)
    },
    GruppeGeplanteAnzahl(Gruppe) {
      if (this.AusgewaehlterTag === null) return 0
      return Gruppe.Slots.filter(Gang => this.PlanFuerTag(this.AusgewaehlterTag, Gang)).length
    },
    async TagPersonenAendern(Delta) {
      const Neu = Math.min(99, Math.max(1, this.TagPersonen + Delta))
      if (Neu === this.TagPersonen) return
      this.TagPersonen = Neu
      // Bereits geplante Gänge des Tages sofort auf die neue Personenzahl umstellen
      const Datum = this.DatumZuString(this.Tage[this.AusgewaehlterTag].Datum)
      const Positionen = this.Wochenplanpositionen.filter(p => p.Datum === Datum)
      if (Positionen.length === 0) return
      this.Fehler = ''
      try {
        await Promise.all(Positionen.map(p => WochenplanpositionSpeichern(p.Datum, p.Kategorie, p.RezeptId, Neu)))
      } catch (e) {
        this.Fehler = e.message
      }
    },
    RezeptAuswaehlen(Gang) {
      this.AktuellerGang = Gang
      this.RezeptSuche = ''
      this.Fehler = ''
      this.RezeptPickerOffen = true
    },
    RezeptPickerSchliessen() {
      this.RezeptPickerOffen = false
    },
    async RezeptWaehlen(Rezept) {
      this.RezeptPickerOffen = false
      this.Fehler = ''
      const Datum = this.DatumZuString(this.Tage[this.AusgewaehlterTag].Datum)
      const Vorherige = this.PlanFuerTag(this.AusgewaehlterTag, this.AktuellerGang)
      try {
        await WochenplanpositionSpeichern(Datum, this.AktuellerGang, Rezept.Id, this.TagPersonen)
      } catch (e) {
        this.Fehler = e.message
        return
      }
      // Planungs-Ranking lokal nachziehen (Server zählt beim nächsten Laden ohnehin frisch)
      if (Vorherige && Vorherige.RezeptId !== Rezept.Id) {
        const AltesRezept = this.Rezepte.find(r => r.Id === Vorherige.RezeptId)
        if (AltesRezept && AltesRezept.PlanungAnzahl > 0) AltesRezept.PlanungAnzahl--
      }
      if (!Vorherige || Vorherige.RezeptId !== Rezept.Id) {
        Rezept.PlanungAnzahl = (Rezept.PlanungAnzahl || 0) + 1
      }
      // Eingefrorene Gerichte sind schon fertig - kein Zutaten-Picker, es muss nichts eingekauft werden
      if (Rezept.Eingefroren) return
      this.AktuellesRezept = Rezept
      this.GewaehlteZutatenIds = (Rezept.Zutaten || []).map(z => z.ZutatId)
      this.ZutatenFehler = ''
      this.ZielListeId = AktiveEinkaufsliste.Id
      this.ZutatenPickerOffen = true
    },
    async PlanLoeschen(Gang) {
      const Position = this.PlanFuerTag(this.AusgewaehlterTag, Gang)
      if (!Position) return
      if (!confirm(`Rezept „${Position.RezeptName}" für ${Gang} entfernen?`)) return
      this.Fehler = ''
      try {
        await WochenplanpositionLoeschen(Position.Id)
      } catch (e) {
        this.Fehler = e.message
        return
      }
      const Rezept = this.Rezepte.find(r => r.Id === Position.RezeptId)
      if (Rezept && Rezept.PlanungAnzahl > 0) Rezept.PlanungAnzahl--
    },
    ZutatenPickerSchliessen() {
      this.ZutatenPickerOffen = false
    },
    async ZutatenHinzufuegen() {
      this.ZutatenFehler = ''
      const EingefroreneNamen = this.GewaehlteZutatenIds
        .filter(ZutatId => this.ZutatEingefroren(ZutatId))
        .map(ZutatId => this.ZutatNameFinden(ZutatId))
      if (EingefroreneNamen.length > 0) {
        const Frage = `${EingefroreneNamen.map(N => `„${N}“`).join(', ')} ${EingefroreneNamen.length === 1 ? 'ist' : 'sind'} als eingefroren markiert. Trotzdem zur Einkaufsliste hinzufügen?`
        if (!confirm(Frage)) return
      }
      this.ZutatenLaedt = true
      let Fehleranzahl = 0
      await Promise.all(
        this.GewaehlteZutatenIds.map(async ZutatId => {
          const Zeile = (this.AktuellesRezept?.Zutaten || []).find(z => z.ZutatId === ZutatId) || {}
          const { Anzahl } = PackungsBedarf(Zeile, ZutatFinden(ZutatId), this.PersonenFaktor)
          try {
            await ZutatZurEinkaufslisteHinzufuegen(ZutatId, this.ZielListeId, Anzahl, this.AktuellesRezept?.Id)
          } catch {
            Fehleranzahl++
          }
        })
      )
      this.ZutatenLaedt = false
      if (Fehleranzahl > 0) {
        this.ZutatenFehler = `${Fehleranzahl} Zutat(en) konnten nicht hinzugefügt werden.`
      } else {
        this.ZutatenPickerOffen = false
      }
    },
    ZutatMengeText(Zeile) {
      // Skalierte Rezeptmenge + daraus folgende Packungsanzahl (z.B. "800 Gramm → 2 × 500 Gramm")
      const Zutat = ZutatFinden(Zeile.ZutatId)
      const { Anzahl, SkalierteMenge } = PackungsBedarf(Zeile, Zutat, this.PersonenFaktor)
      const Teile = []
      if (SkalierteMenge != null) {
        Teile.push(MengeFormatieren(SkalierteMenge, Zeile.Einheit))
      }
      if (Zutat && Zutat.Menge != null) {
        Teile.push(`→ ${Anzahl} × ${MengeFormatieren(Zutat.Menge, Zutat.Einheit)}`)
      } else if (Anzahl > 1) {
        Teile.push(`→ ${Anzahl}×`)
      }
      return Teile.join(' ')
    },
    ZutatNameFinden(ZutatId, FallbackName) {
      const Zutat = ZutatFinden(ZutatId)
      return Zutat ? Zutat.Name : (FallbackName || '?')
    },
    ZutatEingefroren(ZutatId) {
      return !!ZutatFinden(ZutatId)?.Eingefroren
    },
    RezeptBildUrl(Rezept) {
      return BildUrl('Rezept', Rezept.Id, Rezept.BildVersion)
    },
    ZutatBildUrlWennVorhanden(ZutatId) {
      const Zutat = ZutatFinden(ZutatId)
      return Zutat && Zutat.HatBild ? BildUrl('Zutat', Zutat.Id, Zutat.BildVersion) : null
    },
    GangBildUrl(Gang) {
      const Plan = this.AusgewaehlterTag !== null ? this.PlanFuerTag(this.AusgewaehlterTag, Gang) : null
      if (!Plan) return null
      const Rezept = this.Rezepte.find(r => r.Id === Plan.RezeptId)
      return Rezept && Rezept.HatBild ? BildUrl('Rezept', Rezept.Id, Rezept.BildVersion) : null
    },
    MengeAnzeigen(Menge) {
      if (!Menge) return ''
      return +Menge
    }
  }
}
</script>

<style scoped>
.wochen-page {
  max-width: 860px;
}

/* ── Kopf ────────────────────────────────────────────── */
.wochen-kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.wochen-kopf-mitte {
  text-align: center;
  flex: 1;
}

.wochen-titel {
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.2;
}

.wochen-label {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: 2px;
}

.kopf-chips {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 7px;
}

.menue-chip {
  --karte-gold: #A8894B;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border: none;
  border-radius: 999px;
  background: color-mix(in srgb, var(--karte-gold) 14%, transparent);
  color: var(--karte-gold);
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 650;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

html[data-theme="dark"] .menue-chip {
  --karte-gold: #CBB26A;
}

.menue-chip:hover {
  background: color-mix(in srgb, var(--karte-gold) 24%, transparent);
  transform: translateY(-1px);
}

.menue-chip:active {
  transform: scale(0.94);
}

.heute-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border: none;
  border-radius: 999px;
  background: var(--akzent-weich);
  color: var(--color-primary);
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 650;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

.heute-chip:hover {
  background: color-mix(in srgb, var(--color-primary) 24%, transparent);
  transform: translateY(-1px);
}

.heute-chip:active {
  transform: scale(0.94);
}

.nav-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: var(--radius-md);
  border: 1px solid var(--glas-rand);
  background: var(--glas);
  color: var(--color-text);
  cursor: pointer;
  font-size: 0.85rem;
  flex-shrink: 0;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.15s ease;
}

.nav-arrow:hover {
  border-color: var(--akzent-ring);
  color: var(--color-primary);
  transform: translateY(-1px);
}

.nav-arrow:active {
  transform: scale(0.92);
}

/* ── Tage-Grid ───────────────────────────────────────── */
.tage-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

@media (min-width: 600px) {
  .tage-grid { grid-template-columns: repeat(4, 1fr); }
}

@media (min-width: 900px) {
  .tage-grid { grid-template-columns: repeat(7, 1fr); }
}

.tag-zelle {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 12px 4px 14px;
  border-radius: var(--radius-md);
  border: 1px solid var(--glas-rand);
  background: var(--glas);
  color: var(--color-text);
  cursor: pointer;
  min-height: 72px;
  box-shadow: 0 2px 10px rgba(50, 60, 80, 0.05);
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.18s ease, border-color 0.2s ease, box-shadow 0.3s ease;
}

.tag-zelle:hover {
  background: var(--akzent-weich);
  border-color: var(--akzent-ring);
  transform: translateY(-2px);
}

.tag-zelle:active {
  transform: scale(0.95);
}

.tag-zelle.aktiv {
  background: linear-gradient(135deg, var(--color-primary), var(--akzent-tief));
  border-color: transparent;
  color: #fff;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--color-primary) 40%, transparent);
}

.tag-kuerzel {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.65;
}

.tag-zelle.aktiv .tag-kuerzel {
  opacity: 0.85;
}

.tag-tag {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
}

.tag-dot {
  position: absolute;
  bottom: 7px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-accent);
}

.tag-zelle.aktiv .tag-dot {
  background: rgba(255, 255, 255, 0.75);
}

.tag-zelle.heute:not(.aktiv) .tag-kuerzel {
  color: var(--color-primary);
  opacity: 1;
}

.tag-zelle.heute:not(.aktiv) {
  border-color: color-mix(in srgb, var(--color-primary) 40%, transparent);
}

/* ── Tag-Details ─────────────────────────────────────── */
.tag-details {
  background: var(--glas);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-lg);
  box-shadow: var(--glas-schatten);
  padding: 18px;
  animation: SeiteEinblenden 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.tag-details-kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.tag-details-datum {
  font-size: 1.05rem;
  font-weight: 700;
}

.tag-geplant-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: var(--color-accent-light);
  color: var(--color-accent);
  padding: 3px 10px;
  border-radius: 12px;
}

.personen-stepper {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: var(--color-bg-alt);
  border: 1px solid var(--glas-rand);
  border-radius: 999px;
  flex-shrink: 0;
}

.personen-btn {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.personen-btn:hover:not(:disabled) {
  background: var(--akzent-weich);
  color: var(--color-primary);
}

.personen-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.personen-wert {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 44px;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}

.personen-wert svg {
  font-size: 0.7rem;
  opacity: 0.6;
}

/* ── Mahlzeit-Gruppen ────────────────────────────────── */
.gaenge-liste {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mahlzeit-gruppe {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mahlzeit-gruppe-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
  padding: 0 2px;
}

.gruppe-titel {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
}

.gruppe-badge {
  font-size: 0.66rem;
  font-weight: 600;
  background: var(--color-accent-light);
  color: var(--color-accent);
  padding: 2px 8px;
  border-radius: 10px;
}

/* ── Gang-Zeile ──────────────────────────────────────── */
.gang-zeile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--glas-stark);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-md);
  min-height: 58px;
  transition: border-color 0.2s ease;
}

.gang-zeile:hover {
  border-color: var(--akzent-ring);
}

.gang-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.gang-icon--bild {
  object-fit: cover;
}

.gang-inhalt {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.gang-label {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
}

.gang-rezept-btn {
  background: none;
  border: none;
  padding: 0;
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.gang-rezept-btn:hover {
  color: var(--color-primary);
}

.gang-platzhalter {
  font-size: 0.88rem;
  color: var(--color-text-muted);
  font-style: italic;
}

.gang-aktionen {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.gang-aendern-btn {
  padding: 6px 13px;
  border: none;
  border-radius: 999px;
  background: var(--akzent-weich);
  color: var(--color-primary);
  font-size: 0.78rem;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

.gang-aendern-btn:hover {
  background: color-mix(in srgb, var(--color-primary) 24%, transparent);
  transform: translateY(-1px);
}

.gang-loeschen-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  transition: color 0.15s;
}

.gang-loeschen-btn:hover {
  color: var(--color-danger);
}

.gang-plus-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-primary);
  border-radius: 50%;
  background: var(--color-primary-light);
  color: var(--color-primary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: background 0.15s, color 0.15s;
}

.gang-plus-btn:hover {
  background: var(--color-primary);
  color: #fff;
}

/* ── Menü der Woche (Speisekarte im Overlay) ─────────── */
.menue-overlay {
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
}

.speisekarte {
  --karte-gold: #A8894B;
  position: relative;
  width: min(480px, 100%);
  margin: auto;
  padding: 30px 22px 30px;
  text-align: center;
  font-family: Georgia, 'Times New Roman', serif;
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-lg);
  box-shadow: var(--glas-schatten);
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

html[data-theme="dark"] .speisekarte {
  --karte-gold: #CBB26A;
}

/* Feiner Innenrahmen wie bei einer edlen Menükarte */
.speisekarte::before {
  content: '';
  position: absolute;
  inset: 9px;
  border: 1px solid color-mix(in srgb, var(--karte-gold) 40%, transparent);
  border-radius: calc(var(--radius-lg) - 7px);
  pointer-events: none;
}

.karte-ornament {
  color: var(--karte-gold);
  font-size: 0.8rem;
  letter-spacing: 0.5em;
  margin-bottom: 10px;
}

.karte-ornament--unten {
  margin-bottom: 0;
  margin-top: 18px;
}

.karte-titel {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--color-text);
}

.karte-untertitel {
  margin: 6px 0 0;
  font-style: italic;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.karte-trenner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 18px auto 20px;
  max-width: 240px;
}

.karte-trenner-linie {
  flex: 1;
  height: 1px;
  background: color-mix(in srgb, var(--karte-gold) 45%, transparent);
}

.karte-trenner-raute {
  color: var(--karte-gold);
  font-size: 0.55rem;
}

.karte-seitenzahl {
  opacity: 0.65;
}

.karte-gaenge-liste {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.karte-gericht-kategorie {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--karte-gold);
  opacity: 0.75;
  flex-shrink: 0;
}

.karte-gericht {
  display: block;
  font-size: 1.18rem;
  font-style: italic;
  line-height: 1.35;
  color: var(--color-text);
  transition: color 0.2s ease;
}

/* ── Buch-Ansicht: ein Tag pro Seite, mit Bild pro Gang ── */
.buch-tag-titel {
  margin: 0 0 16px;
  font-size: 0.95rem;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--karte-gold);
  text-align: center;
}

.karte-gang-buch {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  text-align: left;
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.karte-gang-buch:hover {
  border-color: color-mix(in srgb, var(--karte-gold) 35%, transparent);
  background: color-mix(in srgb, var(--karte-gold) 6%, transparent);
  transform: translateY(-1px);
}

.karte-gang-buch:hover .karte-gericht {
  color: var(--karte-gold);
}

.karte-gang-bild {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--karte-gold) 35%, transparent);
}

.karte-gang-bild--platzhalter {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--karte-gold);
  opacity: 0.5;
  font-size: 1.2rem;
  background: color-mix(in srgb, var(--karte-gold) 10%, transparent);
}

.karte-gang-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.buch-viewport {
  position: relative;
  perspective: 1400px;
  padding: 4px 34px;
  min-height: 120px;
}

.buch-seite {
  min-height: 100px;
}

.buch-pfeil {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid color-mix(in srgb, var(--karte-gold) 45%, transparent);
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--karte-gold);
  cursor: pointer;
  z-index: 1;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

.buch-pfeil:hover {
  background: color-mix(in srgb, var(--karte-gold) 15%, transparent);
}

.buch-pfeil:active {
  transform: translateY(-50%) scale(0.92);
}

.buch-pfeil--links {
  left: 0;
}

.buch-pfeil--rechts {
  right: 0;
}

.buch-epilog {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 10px;
}

.buch-epilog-ornament {
  color: var(--karte-gold);
  font-size: 1.4rem;
}

.buch-epilog-text {
  margin: 0;
  font-style: italic;
  color: var(--color-text-muted);
}

.buch-epilog-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 10px 22px;
  border: 1px solid color-mix(in srgb, var(--karte-gold) 50%, transparent);
  border-radius: 999px;
  background: transparent;
  color: var(--karte-gold);
  font-family: inherit;
  font-style: italic;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

.buch-epilog-link:hover {
  background: color-mix(in srgb, var(--karte-gold) 12%, transparent);
  transform: translateY(-1px);
}

/* Seitenwechsel-Animation, Richtung abhaengig von Vor/Zurueck (Blaettern wie in einem Buch) */
.buch-seite-vor-enter-active,
.buch-seite-vor-leave-active,
.buch-seite-zurueck-enter-active,
.buch-seite-zurueck-leave-active {
  transition: transform 0.4s cubic-bezier(0.45, 0, 0.4, 1), opacity 0.3s ease;
}

.buch-seite-vor-leave-to {
  transform: rotateY(-18deg) translateX(-10px);
  opacity: 0;
}

.buch-seite-vor-enter-from {
  transform: rotateY(18deg) translateX(10px);
  opacity: 0;
}

.buch-seite-zurueck-leave-to {
  transform: rotateY(18deg) translateX(10px);
  opacity: 0;
}

.buch-seite-zurueck-enter-from {
  transform: rotateY(-18deg) translateX(10px);
  opacity: 0;
}

.karte-leer {
  margin: 0;
  font-style: italic;
  font-size: 0.9rem;
  color: var(--color-text-muted);
}

.karte-schliessen {
  margin-top: 18px;
  padding: 8px 26px;
  border: 1px solid color-mix(in srgb, var(--karte-gold) 45%, transparent);
  border-radius: 999px;
  background: transparent;
  color: var(--karte-gold);
  font-family: inherit;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.15s ease;
}

.karte-schliessen:hover {
  background: color-mix(in srgb, var(--karte-gold) 12%, transparent);
}

.karte-schliessen:active {
  transform: scale(0.95);
}

/* ── Bottom Sheet ────────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  z-index: 200;
  animation: Einblenden 0.25s ease both;
}

.bottom-sheet {
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: 12px 16px 32px;
  width: 100%;
  max-height: 82vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .overlay {
    justify-content: center;
    align-items: center;
  }
  .bottom-sheet {
    border-radius: var(--radius-xl);
    max-width: 480px;
    max-height: 80vh;
    padding: 24px;
  }
}

.sheet-griff {
  width: 40px;
  height: 4px;
  background: var(--color-border);
  border-radius: 2px;
  margin: 0 auto 4px;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .sheet-griff { display: none; }
}

.sheet-titel {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.sheet-rezept-name {
  margin: -4px 0 0;
  font-style: italic;
}

.picker-liste {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 45vh;
}

.picker-eintrag {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  font-size: 0.95rem;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s;
}

.picker-eintrag:hover {
  border-color: var(--color-primary);
}

.picker-eintrag--eingefroren {
  border-color: #6EC1E4;
  background: color-mix(in srgb, #6EC1E4 18%, var(--color-surface));
}

.picker-eintrag--eingefroren:hover {
  border-color: #6EC1E4;
}

.picker-name {
  font-weight: 600;
  flex: 1;
  min-width: 0;
}

.picker-kategorie {
  font-size: 0.78rem;
  opacity: 0.55;
  flex-shrink: 0;
}

.picker-gruppen-titel {
  margin: 4px 0 0;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
}

.picker-kein-rezept {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 4px 0;
}

.neu-rezept-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: unset;
}

.zutaten-check-liste {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 45vh;
}

.zutat-check-zeile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  cursor: pointer;
  transition: border-color 0.15s;
}

.zutat-check-zeile:hover {
  border-color: var(--color-primary);
}

.zutat-check-zeile--eingefroren {
  border-color: #6EC1E4;
  background: color-mix(in srgb, #6EC1E4 18%, var(--color-surface));
}

.zutat-check-zeile--eingefroren:hover {
  border-color: #6EC1E4;
}

.zutat-check-zeile input[type="checkbox"] {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: var(--color-primary);
}

.zutat-check-name {
  flex: 1;
  font-weight: 500;
}

.zutat-check-menge {
  font-size: 0.82rem;
  opacity: 0.55;
  flex-shrink: 0;
}

.ziel-liste-zeile {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ziel-liste-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-muted);
}

.ziel-liste-select {
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 0.95rem;
  font-family: inherit;
  outline: none;
}
</style>
